import React, { useState, useEffect } from 'react';
import {
  Wrench, Plus, Search, Filter, Clock, CheckCircle, XCircle,
  AlertCircle, ChevronDown, ChevronUp, Upload, FileText, Download,
  X, RefreshCw, Calendar, User, MapPin, Phone, Zap, Eye, Edit, Trash2,
  DollarSign, History, TrendingUp, ArrowUpRight, ArrowDownLeft, TrendingDown, Check
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

// Role-based step permission:
// - OWNER, TECHNICIAN, SURVEYOR, INSTALLER, SLO: full access to all steps
// - MANAGER: pascapemasangan steps only (pemberkasan, slo, registrasi_pln, documentation)
// - IT: full access (admin)
const STEP_PERMISSIONS = {
  INSTALLER: ['all'],  // Full access like SURVEYOR
  SURVEYOR: ['all'],   // Full access
  SLO: ['all']         // Full access like SURVEYOR
};

const isStepAllowedForCurrentUser = (user, stepKey) => {
  if (!user) return false;
  const role = user.role;

  // Super users, TECHNICIAN, and specialist roles (SURVEYOR, INSTALLER, SLO) — full access to all steps
  if (['OWNER', 'TECHNICIAN', 'IT', 'SURVEYOR', 'INSTALLER', 'SLO'].includes(role)) return true;

  // MANAGER — limited to pascapemasangan steps
  if (role === 'MANAGER') {
    return ['pemberkasan', 'slo', 'registrasi_pln', 'documentation'].includes(stepKey);
  }

  // Specialist roles (top-level roles) — limited to their designated steps
  if (role in STEP_PERMISSIONS) {
    const permissions = STEP_PERMISSIONS[role];
    if (permissions.includes('all')) return true;
    return permissions.includes(stepKey);
  }

  return false;
};

const TechnicalJobsPage = ({ currentUser: user, api }) => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [technicianRoles, setTechnicianRoles] = useState({});
  const [selectedRole, setSelectedRole] = useState('TECHNICIAN');
  const [selectedTechId, setSelectedTechId] = useState('');
  const [editingAssignment, setEditingAssignment] = useState(false);
  const [uploadingStep, setUploadingStep] = useState(null);
  const [newJob, setNewJob] = useState({
    nomor_registrasi: '',
    nomor_meter: '',
    nama_pelanggan: '',
    alamat: '',
    no_hp: '',
    nomor_ktp: '',
    id_pelanggan_pln: '',
    npwp: '',
    tarif_pemasangan: '',
    gaji_teknisi: '',
    keperluan: '',
    daya_listrik: '900VA',
    daya_lama: '',
    riwayat_daya: [],
    jenis_instalasi: 'BARU',
    lokasi_latitude: '',
    lokasi_longitude: '',
    catatatan: ''
  });
  const [showManualDaya, setShowManualDaya] = useState(false);

  // Tab State
  const [activeTab, setActiveTab] = useState('jobs');

  // Payment Modal States
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Manual Transaction Modal States
  const [showManualTxModal, setShowManualTxModal] = useState(false);
  const [isSavingManualTx, setIsSavingManualTx] = useState(false);
  const [manualTxForm, setManualTxForm] = useState({
    type: 'PEMASUKAN', // PEMASUKAN or PENGELUARAN
    job_id: 'STANDALONE',
    nama_transaksi: '',
    nominal: '',
    tanggal: new Date().toISOString().substring(0, 10),
    catatatan: ''
  });
  const [selectedPaymentJob, setSelectedPaymentJob] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    tarif_pemasangan: '',
    gaji_teknisi: '',
    dikonfirmasi: false,
    tanggal_pembayaran: '',
    catatan: ''
  });

  // Filters for Accounting Tab
  const [accountingSearch, setAccountingSearch] = useState('');
  const [accountingTypeFilter, setAccountingTypeFilter] = useState(''); // 'PEMASUKAN', 'PENGELUARAN_GAJI'
  const [accountingPaymentFilter, setAccountingPaymentFilter] = useState(''); // 'DIBAYAR', 'BELUM_DIBAYAR'

  // Filters for Progress Tab
  const [progressSearch, setProgressSearch] = useState('');
  const [progressStepFilter, setProgressStepFilter] = useState('');

  const isManager = ['OWNER', 'MANAGER', 'IT'].includes(user.role);
  const isTechnician = user.role === 'TECHNICIAN';

  useEffect(() => {
    fetchJobs();
    fetchStats();
    if (isManager) {
      fetchTechnicians();
    }
  }, [filterStatus]);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      let url = `${api}/api/technical-jobs?limit=100`;
      if (filterStatus) {
        url += `&status=${filterStatus}`;
      }
      if (isTechnician) {
        url += `&assigned_to_me=true`;
      }
      const response = await fetch(url, { headers: getHeaders() });
      const data = await response.json();
      setJobs(data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${api}/api/technical-jobs/dashboard/stats`, { headers: getHeaders() });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await fetch(`${api}/api/technical-jobs/technicians`, { headers: getHeaders() });
      const data = await response.json();
      setTechnicians(data.technicians || []);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
  };

  const openPaymentModal = (job) => {
    setSelectedPaymentJob(job);
    setPaymentForm({
      tarif_pemasangan: job.tarif_pemasangan || 0,
      gaji_teknisi: job.gaji_teknisi !== undefined && job.gaji_teknisi !== null 
        ? job.gaji_teknisi 
        : (job.tarif_pemasangan ? Math.round(job.tarif_pemasangan * 0.40) : 0),
      dikonfirmasi: job.status_pembayaran === 'DIBAYAR' || job.pembayaran_dikonfirmasi || false,
      tanggal_pembayaran: job.tanggal_pembayaran ? job.tanggal_pembayaran.substring(0, 10) : new Date().toISOString().substring(0, 10),
      catatan: job.catatan_manager || ''
    });
    setShowPaymentModal(true);
  };

  const handleUpdatePayment = async (jobId, paymentData) => {
    try {
      const params = new URLSearchParams();
      if (paymentData.tarif_pemasangan !== undefined && paymentData.tarif_pemasangan !== '') {
        params.append('tarif_pemasangan', paymentData.tarif_pemasangan);
      }
      if (paymentData.gaji_teknisi !== undefined && paymentData.gaji_teknisi !== '') {
        params.append('gaji_teknisi', paymentData.gaji_teknisi);
      }
      if (paymentData.dikonfirmasi !== undefined) {
        params.append('dikonfirmasi', paymentData.dikonfirmasi);
      }
      if (paymentData.tanggal_pembayaran !== undefined) {
        params.append('tanggal_pembayaran', paymentData.tanggal_pembayaran);
      }
      if (paymentData.catatan !== undefined) {
        params.append('catatan', paymentData.catatan);
      }

      const response = await fetch(
        `${api}/api/technical-jobs/${jobId}/payment?${params.toString()}`,
        {
          method: 'PUT',
          headers: getHeaders()
        }
      );
      const data = await response.json();
      if (response.ok) {
        alert('Data pembayaran & keuangan berhasil diperbarui!');
        setShowPaymentModal(false);
        fetchJobs();
        fetchStats();
      } else {
        alert(data.detail || 'Gagal memperbarui pembayaran');
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('Gagal memperbarui pembayaran');
    }
  };

  const handleCreateManualTx = async (e) => {
    e.preventDefault();
    if (!manualTxForm.nama_transaksi || !manualTxForm.nominal) {
      alert('Nama transaksi dan nominal wajib diisi!');
      return;
    }

    try {
      setIsSavingManualTx(true);

      if (manualTxForm.job_id === 'STANDALONE') {
        // 1. Create the job entry
        const payload = {
          nomor_meter: '-',
          nama_pelanggan: manualTxForm.nama_transaksi,
          alamat: '-',
          no_hp: '-',
          nomor_ktp: '-',
          id_pelanggan_pln: '-',
          npwp: '-',
          daya_listrik: '900VA',
          jenis_instalasi: manualTxForm.type === 'PEMASUKAN' ? 'PEMASUKAN_MANUAL' : 'PENGELUARAN_MANUAL',
          tarif_pemasangan: manualTxForm.type === 'PEMASUKAN' ? parseFloat(manualTxForm.nominal) : 0,
          gaji_teknisi: manualTxForm.type === 'PENGELUARAN' ? parseFloat(manualTxForm.nominal) : 0,
          lokasi_latitude: null,
          lokasi_longitude: null,
          catatatan: manualTxForm.catatatan || ''
        };

        const createResponse = await fetch(`${api}/api/technical-jobs`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(payload)
        });

        const createData = await createResponse.json();
        if (!createResponse.ok) {
          throw new Error(createData.detail || 'Gagal membuat catatan transaksi');
        }

        const jobId = createData.job_id;

        // 2. Mark as completed
        const updateResponse = await fetch(`${api}/api/technical-jobs/${jobId}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({
            status: 'completed'
          })
        });

        if (!updateResponse.ok) {
          const updateData = await updateResponse.json();
          throw new Error(updateData.detail || 'Gagal mengupdate status transaksi');
        }

        // 3. Mark payment as paid
        const params = new URLSearchParams();
        params.append('dikonfirmasi', 'true');
        params.append('tanggal_pembayaran', manualTxForm.tanggal);
        params.append('catatan', manualTxForm.catatatan || '');

        const paymentResponse = await fetch(
          `${api}/api/technical-jobs/${jobId}/payment?${params.toString()}`,
          {
            method: 'PUT',
            headers: getHeaders()
          }
        );

        if (!paymentResponse.ok) {
          const paymentData = await paymentResponse.json();
          throw new Error(paymentData.detail || 'Gagal mengonfirmasi pembayaran transaksi');
        }
      } else {
        // Existing operational job
        const jobId = manualTxForm.job_id;
        const matched = jobs.find(j => j.id === jobId);
        if (!matched) {
          throw new Error('Pekerjaan tidak ditemukan di sistem');
        }

        const nominalVal = parseFloat(manualTxForm.nominal);
        let finalTarif = matched.tarif_pemasangan || 0;
        let finalGaji = matched.gaji_teknisi !== undefined && matched.gaji_teknisi !== null 
          ? matched.gaji_teknisi 
          : (matched.tarif_pemasangan ? Math.round(matched.tarif_pemasangan * 0.40) : 0);

        if (manualTxForm.type === 'PEMASUKAN') {
          finalTarif = nominalVal;
          if (matched.gaji_teknisi === undefined || matched.gaji_teknisi === null || matched.gaji_teknisi === 0) {
            finalGaji = Math.round(finalTarif * 0.40);
          }
        } else {
          finalGaji = nominalVal;
        }

        // 1. Update Payment status & values
        const params = new URLSearchParams();
        params.append('tarif_pemasangan', finalTarif.toString());
        params.append('gaji_teknisi', finalGaji.toString());
        params.append('dikonfirmasi', 'true');
        params.append('tanggal_pembayaran', manualTxForm.tanggal);
        params.append('catatan', manualTxForm.catatatan || '');

        const paymentResponse = await fetch(
          `${api}/api/technical-jobs/${jobId}/payment?${params.toString()}`,
          {
            method: 'PUT',
            headers: getHeaders()
          }
        );

        if (!paymentResponse.ok) {
          const paymentData = await paymentResponse.json();
          throw new Error(paymentData.detail || 'Gagal mengupdate pembayaran pekerjaan');
        }

        // 2. Mark status as completed
        const updateResponse = await fetch(`${api}/api/technical-jobs/${jobId}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({
            status: 'completed'
          })
        });

        if (!updateResponse.ok) {
          const updateData = await updateResponse.json();
          throw new Error(updateData.detail || 'Gagal mengupdate status pekerjaan menjadi Selesai');
        }
      }

      alert('Transaksi manual berhasil disimpan!');
      setShowManualTxModal(false);
      setManualTxForm({
        type: 'PEMASUKAN',
        job_id: 'STANDALONE',
        nama_transaksi: '',
        nominal: '',
        tanggal: new Date().toISOString().substring(0, 10),
        catatatan: ''
      });
      fetchJobs();
      fetchStats();
    } catch (error) {
      console.error('Error creating manual transaction:', error);
      alert(error.message || 'Gagal membuat transaksi manual');
    } finally {
      setIsSavingManualTx(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    if (!newJob.nomor_registrasi && !newJob.nomor_meter) {
      alert('Nomor Registrasi wajib diisi');
      return;
    }
    if (newJob.keperluan === 'TAMBAH_DAYA') {
      if (!newJob.id_pelanggan_pln) {
        alert('ID Pelanggan PLN wajib diisi untuk Tambah Daya');
        return;
      }
      if (!newJob.daya_lama) {
        alert('Daya Listrik (lama) wajib diisi untuk Tambah Daya');
        return;
      }
      if (newJob.daya_listrik && newJob.daya_lama && newJob.daya_listrik === newJob.daya_lama) {
        alert('Daya Listrik (baru) tidak boleh sama dengan Daya Listrik (lama)');
        return;
      }
      // Build riwayat: previous entries (if any) + this addition
      const today = new Date().toISOString().slice(0, 10);
      const history = Array.isArray(newJob.riwayat_daya) ? newJob.riwayat_daya : [];
      newJob.riwayat_daya = [
        ...history,
        {
          dari: newJob.daya_lama || null,
          ke: newJob.daya_listrik || null,
          tanggal: today,
          keperluan: newJob.keperluan || null
        }
      ];
    }
    try {
      const response = await fetch(`${api}/api/technical-jobs`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(newJob)
      });
      const data = await response.json();
      if (response.ok) {
        setShowCreateModal(false);
        setShowManualDaya(false);
        setNewJob({
          nomor_registrasi: '',
          nomor_meter: '',
          nama_pelanggan: '',
          alamat: '',
          no_hp: '',
          nomor_ktp: '',
          id_pelanggan_pln: '',
          npwp: '',
          tarif_pemasangan: '',
          gaji_teknisi: '',
          keperluan: '',
          daya_listrik: '900VA',
          daya_lama: '',
          riwayat_daya: [],
          jenis_instalasi: 'BARU',
          lokasi_latitude: '',
          lokasi_longitude: '',
          catatatan: ''
        });
        fetchJobs();
        fetchStats();
      } else {
        alert(data.detail || 'Gagal membuat job');
      }
    } catch (error) {
      console.error('Error creating job:', error);
      alert('Gagal membuat job');
    }
  };

  const handleAssignJob = async (technicianId) => {
    try {
      const response = await fetch(
        `${api}/api/technical-jobs/${selectedJob.id}/assign?technician_id=${technicianId}`,
        { method: 'PUT', headers: getHeaders() }
      );
      if (response.ok) {
        setShowAssignModal(false);
        fetchJobs();
        fetchStats();
      } else {
        const data = await response.json();
        alert(data.detail || 'Gagal menugaskan job');
      }
    } catch (error) {
      console.error('Error assigning job:', error);
      alert('Gagal menugaskan job');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      SURVEYOR: 'Surveyor',
      INSTALLER: 'Installer',
      SLO: 'Petugas SLO',
      TECHNICIAN: 'Teknisi Umum'
    };
    return labels[role] || role;
  };

  const addToAssignment = () => {
    if (!selectedTechId || !selectedRole) {
      alert('Pilih teknisi dan peran terlebih dahulu');
      return;
    }
    setTechnicianRoles((prev) => ({
      ...prev,
      [selectedTechId]: selectedRole
    }));
    setSelectedTechId('');
    setSelectedRole('TECHNICIAN');
  };

  const removeTechnician = (techId) => {
    setTechnicianRoles((prev) => {
      const next = { ...prev };
      delete next[techId];
      return next;
    });
  };

  const confirmMultiAssign = async () => {
    if (Object.keys(technicianRoles).length === 0) {
      alert('Pilih minimal satu teknisi terlebih dahulu');
      return;
    }
    try {
      const technicians = Object.entries(technicianRoles).map(([techId, role]) => ({
        technician_id: techId,
        assigned_role: role
      }));
      const endpoint = editingAssignment
        ? `/api/technical-jobs/${selectedJob.id}/replace-assignments`
        : `/api/technical-jobs/${selectedJob.id}/assign-multiple`;
      const response = await fetch(`${api}${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getHeaders() },
        body: JSON.stringify({ technicians })
      });
      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Berhasil memperbarui penugasan');
        setShowAssignModal(false);
        setTechnicianRoles({});
        setSelectedTechId('');
        setSelectedRole('TECHNICIAN');
        setEditingAssignment(false);
        fetchJobs();
        fetchStats();
      } else {
        const data = await response.json();
        alert(data.detail || 'Gagal memperbarui penugasan');
      }
    } catch (error) {
      console.error('Error updating technician assignments:', error);
      alert('Gagal memperbarui penugasan');
    }
  };

  const handleUpdateProgress = async (stepName, isCompleted, catatatan = '') => {
    try {
      const body = {
        step_name: stepName,
        is_completed: isCompleted,
        catatatan: catatatan || undefined
      };
      const response = await fetch(
        `${api}/api/technical-jobs/${selectedJob.id}/progress`,
        { method: 'PUT', headers: getHeaders(), body: JSON.stringify(body) }
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedJob(data.job);
        fetchJobs();
        fetchStats();
      } else {
        const data = await response.json();
        alert(data.detail || 'Gagal update progress');
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      alert('Gagal update progress');
    }
  };

  const handleUploadEvidence = async (stepName, files) => {
    try {
      setUploadingStep(stepName);
      const fileArray = Array.isArray(files) ? files : [files];
      const formData = new FormData();
      formData.append('step_name', stepName);
      fileArray.forEach((f) => formData.append('files', f));

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${api}/api/technical-jobs/${selectedJob.id}/upload-evidence`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData }
      );

      if (response.ok) {
        const jobResponse = await fetch(`${api}/api/technical-jobs/${selectedJob.id}`, { headers: getHeaders() });
        const jobData = await jobResponse.json();
        setSelectedJob(jobData);
        fetchJobs();
        alert(`${fileArray.length} bukti berhasil diupload!`);
      } else {
        const data = await response.json();
        alert(data.detail || 'Gagal upload file');
      }
    } catch (error) {
      console.error('Error uploading evidence:', error);
      alert('Gagal upload file');
    } finally {
      setUploadingStep(null);
    }
  };

  const handleSetStepData = async (stepKey, payload) => {
    if (!selectedJob) return;
    try {
      const token = localStorage.getItem('token');
      const body = { step_name: stepKey, ...payload };
      const response = await fetch(`${api}/api/technical-jobs/${selectedJob.id}/step-data`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const jobResponse = await fetch(`${api}/api/technical-jobs/${selectedJob.id}`, { headers: getHeaders() });
        const jobData = await jobResponse.json();
        setSelectedJob(jobData);
      } else {
        const data = await response.json();
        alert(data.detail || 'Gagal menyimpan data tahap');
      }
    } catch (error) {
      console.error('Error saving step data:', error);
    }
  };

  const getChecklistForStage = (stageKey) => {
    switch (stageKey) {
      case 'survey_install':
        return [
          { key: 'foto_persil', label: 'Foto situasi lokasi/persil' },
          { key: 'foto_bangunan', label: 'Foto bangunan & titik meter' },
          { key: 'denah_jaringan', label: 'Sketsa/denah jaringan listrik' }
        ];
      case 'installation':
        return [
          { key: 'kabel_sesuai_sni', label: 'Penggunaan kabel sesuai SNI' },
          { key: 'pembumian_terpasang', label: 'Pembumian (grounding) terpasang' },
          { key: 'mcb_terpasang', label: 'MCB/MCB box terpasang rapi' },
          { key: 'foto_instalasi', label: 'Foto dokumentasi instalasi selesai' }
        ];
      case 'slo':
        return [
          { key: 'uji_sesuai_sni', label: 'Pengujian sesuai SNI' },
          { key: 'dokumen_slo', label: 'Dokumen SLO ter-generate' }
        ];
      case 'documentation':
        return [
          { key: 'foto_kwh_meter', label: 'Foto pemasangan KWH meter' },
          { key: 'berita_acara', label: 'Berita acara serah terima' },
          { key: 'laporan_teknisi', label: 'Laporan teknis teknisi' }
        ];
      default:
        return [];
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!confirm('Apakah Anda yakin ingin menghapus job ini?')) return;
    try {
      const response = await fetch(`${api}/api/technical-jobs/${jobId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (response.ok) {
        fetchJobs();
        fetchStats();
      } else {
        const data = await response.json();
        alert(data.detail || 'Gagal menghapus job');
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Gagal menghapus job');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRupiah = (num) => {
    return `Rp ${(num || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Menunggu' },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw, label: 'Dikerjakan' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Selesai' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Dibatalkan' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const filteredJobs = jobs.filter(job => {
    if (job.jenis_instalasi === 'PEMASUKAN_MANUAL' || job.jenis_instalasi === 'PENGELUARAN_MANUAL') {
      return false;
    }
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        job.nomor_meter?.toLowerCase().includes(search) ||
        job.nama_pelanggan?.toLowerCase().includes(search) ||
        job.alamat?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const jobStages = [
    { key: 'survey_install', label: 'Survey Instalasi', weight: 40, kategori: 'SURVEYOR_INSTALL' },
    { key: 'pemberkasan', label: 'Pemberkasan & Pengajuan PLN', weight: 5, kategori: 'MANAGER' },
    { key: 'installation', label: 'Instalasi Listrik', weight: 20, kategori: 'INSTALLER' },
    { key: 'survey_slo', label: 'Survey SLO', weight: 10, kategori: 'SURVEYOR_SLO' },
    { key: 'slo', label: 'Sertifikat SLO', weight: 15, kategori: 'MANAGER' },
    { key: 'registrasi_pln', label: 'Registrasi Aplikasi PLN', weight: 5, kategori: 'MANAGER' },
    { key: 'documentation', label: 'Dokumentasi & Laporan', weight: 5, kategori: 'MANAGER' }
  ];

  // Dynamic calculations for Accounting (Cash & Accrual basis)
  const totalOmset = jobs.reduce((sum, j) => sum + (j.tarif_pemasangan || 0), 0);
  
  const pemasukanLunas = jobs.reduce((sum, j) => {
    const isPaid = j.status_pembayaran === 'DIBAYAR' || j.pembayaran_dikonfirmasi || false;
    return isPaid ? sum + (j.tarif_pemasangan || 0) : sum;
  }, 0);

  const piutangBelumBayar = totalOmset - pemasukanLunas;

  const totalKewajibanGaji = jobs.reduce((sum, j) => {
    if (j.jenis_instalasi === 'PEMASUKAN_MANUAL') return sum;
    const commission = j.gaji_teknisi !== undefined && j.gaji_teknisi !== null 
      ? j.gaji_teknisi 
      : (j.tarif_pemasangan ? Math.round(j.tarif_pemasangan * 0.40) : 0);
    return sum + commission;
  }, 0);

  const gajiTerbayar = jobs.reduce((sum, j) => {
    if (j.jenis_instalasi === 'PEMASUKAN_MANUAL') return sum;
    const isPaid = j.status_pembayaran === 'DIBAYAR' || j.pembayaran_dikonfirmasi || false;
    const commission = j.gaji_teknisi !== undefined && j.gaji_teknisi !== null 
      ? j.gaji_teknisi 
      : (j.tarif_pemasangan ? Math.round(j.tarif_pemasangan * 0.40) : 0);
    return isPaid ? sum + commission : sum;
  }, 0);

  const hutangGaji = totalKewajibanGaji - gajiTerbayar;

  const labaBersihKas = pemasukanLunas - gajiTerbayar;
  const labaBersihEstimasi = totalOmset - totalKewajibanGaji;

  // Margin Keuntungan Realisasi (Completed Operational Jobs + Manual Incomes & Expenses)
  const omsetSelesai = jobs.reduce((sum, j) => {
    if (j.status !== 'completed') return sum;
    return sum + (j.tarif_pemasangan || 0);
  }, 0);

  const pengeluaranSelesai = jobs.reduce((sum, j) => {
    if (j.status !== 'completed') return sum;
    if (j.jenis_instalasi === 'PEMASUKAN_MANUAL') return sum;
    const commission = j.gaji_teknisi !== undefined && j.gaji_teknisi !== null 
      ? j.gaji_teknisi 
      : (j.tarif_pemasangan ? Math.round(j.tarif_pemasangan * 0.40) : 0);
    return sum + commission;
  }, 0);

  const labaSelesai = omsetSelesai - pengeluaranSelesai;
  const marginLabaEstimasi = omsetSelesai > 0 ? (labaSelesai / omsetSelesai) * 100 : 0;

  // Ledger entries (Generated from all jobs with finance values)
  const ledgerEntries = [];
  jobs.forEach(job => {
    if (!job.tarif_pemasangan && !job.gaji_teknisi) return;

    const isPaid = job.status_pembayaran === 'DIBAYAR' || job.pembayaran_dikonfirmasi || false;
    const itemDate = job.tanggal_pembayaran || job.completed_at || job.updated_at || job.created_at;

    if (job.jenis_instalasi === 'PEMASUKAN_MANUAL') {
      ledgerEntries.push({
        id: `${job.id}-pemasukan`,
        jobId: job.id,
        job: job,
        tanggal: itemDate,
        tipe: 'PEMASUKAN',
        kategori: 'PEMASUKAN_MANUAL',
        keterangan: `Pemasukan Manual: ${job.nama_pelanggan}${job.catatatan ? ` (${job.catatatan})` : ''}`,
        nomor_meter: job.nomor_meter || '-',
        nominal: job.tarif_pemasangan || 0,
        status_pembayaran: isPaid ? 'DIBAYAR' : 'BELUM_DIBAYAR',
        catatan_manager: job.catatan_manager || job.catatatan,
        jobStatus: job.status
      });
      return;
    }

    if (job.jenis_instalasi === 'PENGELUARAN_MANUAL') {
      ledgerEntries.push({
        id: `${job.id}-pengeluaran`,
        jobId: job.id,
        job: job,
        tanggal: itemDate,
        tipe: 'PENGELUARAN_GAJI',
        kategori: 'PENGELUARAN_MANUAL',
        keterangan: `Pengeluaran Manual: ${job.nama_pelanggan}${job.catatatan ? ` (${job.catatatan})` : ''}`,
        nomor_meter: job.nomor_meter || '-',
        nominal: job.gaji_teknisi || 0,
        status_pembayaran: isPaid ? 'DIBAYAR' : 'BELUM_DIBAYAR',
        catatan_manager: job.catatan_manager || job.catatatan,
        jobStatus: job.status
      });
      return;
    }

    // Standard entries
    ledgerEntries.push({
      id: `${job.id}-pemasukan`,
      jobId: job.id,
      job: job,
      tanggal: itemDate,
      tipe: 'PEMASUKAN',
      kategori: job.jenis_instalasi,
      keterangan: `Pemasukan Instalasi ${job.jenis_instalasi} - ${job.nama_pelanggan}`,
      nomor_meter: job.nomor_meter,
      nominal: job.tarif_pemasangan || 0,
      status_pembayaran: isPaid ? 'DIBAYAR' : 'BELUM_DIBAYAR',
      catatan_manager: job.catatan_manager,
      jobStatus: job.status
    });

    const commission = job.gaji_teknisi !== undefined && job.gaji_teknisi !== null
      ? job.gaji_teknisi
      : (job.tarif_pemasangan ? Math.round(job.tarif_pemasangan * 0.40) : 0);

    ledgerEntries.push({
      id: `${job.id}-pengeluaran`,
      jobId: job.id,
      job: job,
      tanggal: itemDate,
      tipe: 'PENGELUARAN_GAJI',
      kategori: 'GAJI_TEKNISI',
      keterangan: `Beban Gaji Teknisi (${job.assigned_to_name || 'Belum Ditugaskan'}) - ${job.nama_pelanggan}`,
      nomor_meter: job.nomor_meter,
      nominal: commission,
      status_pembayaran: isPaid ? 'DIBAYAR' : 'BELUM_DIBAYAR',
      catatan_manager: job.catatan_manager,
      jobStatus: job.status
    });
  });
  ledgerEntries.sort((a, b) => new Date(b.tanggal) - new Date(a.tanggal));

  const filteredLedger = ledgerEntries.filter(entry => {
    const matchesSearch = 
      entry.keterangan.toLowerCase().includes(accountingSearch.toLowerCase()) ||
      entry.nomor_meter.toLowerCase().includes(accountingSearch.toLowerCase()) ||
      entry.job.nama_pelanggan.toLowerCase().includes(accountingSearch.toLowerCase());
    
    const matchesType = !accountingTypeFilter || entry.tipe === accountingTypeFilter;
    const matchesPayment = !accountingPaymentFilter || entry.status_pembayaran === accountingPaymentFilter;

    return matchesSearch && matchesType && matchesPayment;
  });

  // Timeline
  const progressHistory = [];
  jobs.forEach(job => {
    if (job.jenis_instalasi === 'PEMASUKAN_MANUAL' || job.jenis_instalasi === 'PENGELUARAN_MANUAL') return;
    if (job.steps && Array.isArray(job.steps)) {
      job.steps.forEach(step => {
        if (step.is_completed) {
          progressHistory.push({
            id: `${job.id}-${step.step_name}`,
            jobId: job.id,
            nomor_meter: job.nomor_meter,
            nama_pelanggan: job.nama_pelanggan,
            jenis_instalasi: job.jenis_instalasi,
            step_name: step.step_name,
            completed_at: step.completed_at,
            completed_by: step.completed_by,
            catatatan: step.catatatan,
            evidence_files: step.evidence_files || [],
            job: job
          });
        }
      });
    }
  });
  progressHistory.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

  const filteredProgress = progressHistory.filter(item => {
    const matchesSearch = 
      item.nama_pelanggan.toLowerCase().includes(progressSearch.toLowerCase()) ||
      item.nomor_meter.toLowerCase().includes(progressSearch.toLowerCase()) ||
      (item.completed_by || '').toLowerCase().includes(progressSearch.toLowerCase());
    return matchesSearch && (!progressStepFilter || item.step_name === progressStepFilter);
  });

  const handleViewJobDetails = (job) => {
    setSelectedJob(job);
    setShowDetailModal(true);
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="text-primary-600" />
            Pekerjaan Teknis
          </h1>
          <p className="text-gray-600 mt-1">Kelola pekerjaan instalasi listrik dan layanan teknis</p>
        </div>
        {isManager && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => {
                setManualTxForm({
                  type: 'PEMASUKAN',
                  job_id: 'STANDALONE',
                  nama_transaksi: '',
                  nominal: '',
                  tanggal: new Date().toISOString().substring(0, 10),
                  catatatan: ''
                });
                setShowManualTxModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-sm font-semibold text-sm"
            >
              <DollarSign size={18} />
              Catat Keuangan Manual
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm font-semibold text-sm"
            >
              <Plus size={18} />
              Buat Job Baru
            </button>
          </div>
        )}
      </div>

      {/* Tabs Switcher for Managers */}
      {isManager && (
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 text-sm font-medium transition-all ${
              activeTab === 'jobs'
                ? 'border-primary-600 text-primary-600 bg-primary-50/30'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Wrench size={18} />
            Daftar Pekerjaan
          </button>
          <button
            onClick={() => setActiveTab('accounting')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 text-sm font-medium transition-all ${
              activeTab === 'accounting'
                ? 'border-primary-600 text-primary-600 bg-primary-50/30'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DollarSign size={18} />
            Riwayat Akuntansi
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`flex items-center gap-2 px-6 py-3 border-b-2 text-sm font-medium transition-all ${
              activeTab === 'progress'
                ? 'border-primary-600 text-primary-600 bg-primary-50/30'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <History size={18} />
            Riwayat Progress
          </button>
        </div>
      )}

      {activeTab === 'jobs' ? (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Job</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_jobs}</p>
                  </div>
                  <Wrench className="text-primary-600" size={24} />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Menunggu</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.pending_jobs}</p>
                  </div>
                  <Clock className="text-yellow-600" size={24} />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Dikerjakan</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.in_progress_jobs}</p>
                  </div>
                  <RefreshCw className="text-blue-600" size={24} />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Selesai</p>
                    <p className="text-2xl font-bold text-green-600">{stats.completed_jobs}</p>
                  </div>
                  <CheckCircle className="text-green-600" size={24} />
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Rata-rata Progress</p>
                    <p className="text-2xl font-bold text-purple-600">{stats.avg_progress}%</p>
                  </div>
                  <Zap className="text-purple-600" size={24} />
                </div>
              </div>
            </div>
          )}

          {/* Charts */}
          {stats && stats.by_type && stats.by_type.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-4">Job berdasarkan Jenis Instalasi</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={stats.by_type}
                      dataKey="count"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {stats.by_type.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Cari nomor meter, nama pelanggan, atau alamat..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Semua Status</option>
              <option value="pending">Menunggu</option>
              <option value="in_progress">Dikerjakan</option>
              <option value="completed">Selesai</option>
              <option value="cancelled">Dibatalkan</option>
            </select>
            <button
              onClick={fetchJobs}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw size={18} />
              Refresh
            </button>
          </div>

          {/* Jobs Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nomor Registrasi</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pelanggan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alamat</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jenis</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teknisi</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                        <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                        Memuat data...
                      </td>
                    </tr>
                  ) : filteredJobs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                        <Wrench className="mx-auto mb-2" size={24} />
                        Tidak ada job ditemukan
                      </td>
                    </tr>
                  ) : (
                    filteredJobs.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{job.nomor_registrasi || job.nomor_meter || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{job.nama_pelanggan}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{job.alamat}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{job.jenis_instalasi}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary-600 rounded-full"
                                style={{ width: `${job.progress || 0}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600">{job.progress || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(job.status)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {job.assigned_technicians && job.assigned_technicians.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {job.assigned_technicians.map((t, idx) => (
                                <span key={idx} className="inline-flex items-center gap-1">
                                  <User size={12} className="text-gray-400" />
                                  <span className="text-xs">
                                    {t.nama_lengkap || t.username}
                                    {t.assigned_role && (
                                      <span className="ml-1 text-xs text-blue-600">({getRoleLabel(t.assigned_role)})</span>
                                    )}
                                  </span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span>{job.assigned_to_name || '-'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => { setSelectedJob(job); setShowDetailModal(true); }}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              title="Lihat Detail"
                            >
                              <Eye size={18} />
                            </button>
                            {isManager && job.status !== 'completed' && (
                              <button
                                onClick={() => {
                                  setSelectedJob(job);
                                  const isEdit =
                                    job.assigned_technicians &&
                                    job.assigned_technicians.length > 0;
                                  if (isEdit) {
                                    const existing = {};
                                    job.assigned_technicians.forEach((t) => {
                                      existing[t.id] =
                                        t.assigned_role || 'TECHNICIAN';
                                    });
                                    setTechnicianRoles(existing);
                                    setEditingAssignment(true);
                                  } else {
                                    setTechnicianRoles({});
                                    setEditingAssignment(false);
                                  }
                                  setShowAssignModal(true);
                                }}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title={
                                  job.assigned_technicians && job.assigned_technicians.length > 0
                                    ? 'Edit Penugasan'
                                    : 'Tugaskan / Tambah Teknisi'
                                }
                              >
                                <User size={18} />
                              </button>
                            )}
                            {isManager && (
                              <button
                                onClick={() => handleDeleteJob(job.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Hapus"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : activeTab === 'accounting' ? (
        <div className="space-y-6">
          {/* Accounting Stats Cards - Simplified */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Pemasukan */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md p-6 text-white transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-xs font-semibold uppercase tracking-wider">Total Pemasukan</p>
                  <p className="text-3xl font-extrabold mt-1">{formatRupiah(pemasukanLunas)}</p>
                </div>
                <div className="p-3 bg-white/20 rounded-lg">
                  <ArrowUpRight size={28} className="text-white" />
                </div>
              </div>
              <div className="text-xs text-emerald-100 mt-4 flex flex-col gap-1 border-t border-white/20 pt-3">
                <div className="flex justify-between">
                  <span>Piutang (Belum Bayar):</span>
                  <span className="font-bold">{formatRupiah(piutangBelumBayar)}</span>
                </div>
                <div className="flex justify-between text-[10px] text-emerald-200">
                  <span>Total Omset Kontrak:</span>
                  <span>{formatRupiah(totalOmset)}</span>
                </div>
              </div>
            </div>

            {/* Total Pengeluaran */}
            <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl shadow-md p-6 text-white transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rose-100 text-xs font-semibold uppercase tracking-wider">Total Pengeluaran</p>
                  <p className="text-3xl font-extrabold mt-1">{formatRupiah(gajiTerbayar)}</p>
                </div>
                <div className="p-3 bg-white/20 rounded-lg">
                  <ArrowDownLeft size={28} className="text-white" />
                </div>
              </div>
              <div className="text-xs text-rose-100 mt-4 flex flex-col gap-1 border-t border-white/20 pt-3">
                <div className="flex justify-between">
                  <span>Hutang Gaji Teknisi:</span>
                  <span className="font-bold">{formatRupiah(hutangGaji)}</span>
                </div>
                <div className="flex justify-between text-[10px] text-rose-200">
                  <span>Total Liabilitas Komisi:</span>
                  <span>{formatRupiah(totalKewajibanGaji)}</span>
                </div>
              </div>
            </div>

            {/* Laba Bersih */}
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md p-6 text-white transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs font-semibold uppercase tracking-wider">Laba Bersih (Real)</p>
                  <p className="text-3xl font-extrabold mt-1">{formatRupiah(labaBersihKas)}</p>
                </div>
                <div className="p-3 bg-white/20 rounded-lg">
                  <DollarSign size={28} className="text-white" />
                </div>
              </div>
              <div className="text-xs text-blue-100 mt-4 flex flex-col gap-1 border-t border-white/20 pt-3">
                <div className="flex justify-between">
                  <span>Margin Keuntungan:</span>
                  <span className="font-bold">{marginLabaEstimasi.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between text-[10px] text-blue-200">
                  <span>Pekerjaan Selesai:</span>
                  <span>{jobs.filter(j => j.status === 'completed').length} / {jobs.length} ({jobs.filter(j => j.status_pembayaran === 'DIBAYAR' || j.pembayaran_dikonfirmasi).length} lunas)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filters for Accounting */}
          <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Cari transaksi (pelanggan, meter, keterangan)..."
                value={accountingSearch}
                onChange={(e) => setAccountingSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <select
              value={accountingTypeFilter}
              onChange={(e) => setAccountingTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Semua Jenis Transaksi</option>
              <option value="PEMASUKAN">Pemasukan</option>
              <option value="PENGELUARAN_GAJI">Pengeluaran</option>
            </select>
            <select
              value={accountingPaymentFilter}
              onChange={(e) => setAccountingPaymentFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Semua Status Pembayaran</option>
              <option value="DIBAYAR">Lunas / Dikonfirmasi</option>
              <option value="BELUM_DIBAYAR">Belum Dibayar</option>
            </select>
          </div>

          {/* Accounting Ledger Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tanggal</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Nomor Meter / Pelanggan</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Deskripsi Transaksi</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tipe</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Nominal</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Pembayaran</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredLedger.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                        <DollarSign className="mx-auto mb-2 text-gray-400" size={32} />
                        Tidak ada transaksi akuntansi ditemukan
                      </td>
                    </tr>
                  ) : (
                    filteredLedger.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(entry.tanggal)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <p className="font-semibold text-gray-900">{entry.nomor_meter}</p>
                          <p className="text-gray-500 text-xs font-medium mb-1">{entry.job.nama_pelanggan}</p>
                          <div className="flex gap-1.5 flex-wrap items-center mt-1">
                            {getStatusBadge(entry.jobStatus)}
                            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono uppercase">
                              {entry.job.jenis_instalasi}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-sm">
                          <p className="font-medium">{entry.keterangan}</p>
                          {entry.catatan_manager && (
                            <p className="text-xs text-amber-600 italic bg-amber-50 px-2 py-0.5 rounded mt-1 inline-block">
                              Catatan: {entry.catatan_manager}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {entry.tipe === 'PEMASUKAN' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full border border-emerald-200">
                              <ArrowUpRight size={12} />
                              Pemasukan
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-100 text-rose-800 text-xs font-bold rounded-full border border-rose-200">
                              <ArrowDownLeft size={12} />
                              Pengeluaran
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-bold whitespace-nowrap">
                          <span className={entry.tipe === 'PEMASUKAN' ? 'text-emerald-600' : 'text-rose-600'}>
                            {entry.tipe === 'PEMASUKAN' ? '+' : '-'} {formatRupiah(entry.nominal)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {entry.status_pembayaran === 'DIBAYAR' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              Lunas / Dikonfirmasi
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200 animate-pulse">
                              Belum Dibayar
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openPaymentModal(entry.job)}
                              className="px-2.5 py-1 bg-primary-50 text-primary-700 hover:bg-primary-100 rounded border border-primary-200 text-xs font-semibold flex items-center gap-1 transition-colors"
                            >
                              <Edit size={12} />
                              Kelola Keuangan
                            </button>
                            {entry.status_pembayaran !== 'DIBAYAR' && (
                              <button
                                onClick={() => {
                                  if (confirm('Konfirmasi transaksi ini telah lunas?')) {
                                    handleUpdatePayment(entry.jobId, {
                                      tarif_pemasangan: entry.job.tarif_pemasangan,
                                      gaji_teknisi: entry.job.gaji_teknisi !== undefined && entry.job.gaji_teknisi !== null 
                                        ? entry.job.gaji_teknisi 
                                        : (entry.job.tarif_pemasangan ? Math.round(entry.job.tarif_pemasangan * 0.4) : 0),
                                      dikonfirmasi: true,
                                      tanggal_pembayaran: new Date().toISOString()
                                    });
                                  }
                                }}
                                className="px-2.5 py-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded text-xs font-semibold flex items-center gap-1 transition-colors shadow-sm"
                              >
                                <Check size={12} />
                                Lunas
                              </button>
                            )}
                            {isManager && (
                              <button
                                onClick={() => {
                                  if (confirm('Apakah Anda yakin ingin menghapus catatan transaksi ini?')) {
                                    handleDeleteJob(entry.jobId);
                                  }
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded border border-red-200 text-xs font-semibold flex items-center gap-1 transition-colors"
                                title="Hapus Catatan Keuangan"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress Filters */}
          <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Cari progress (pelanggan, meter, teknisi)..."
                value={progressSearch}
                onChange={(e) => setProgressSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <select
              value={progressStepFilter}
              onChange={(e) => setProgressStepFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Semua Tahapan</option>
              <option value="survey">Survey Lokasi</option>
              <option value="installation">Instalasi Listrik</option>
              <option value="nidi_slo">NIDI / SLO</option>
              <option value="documentation">Dokumentasi</option>
              <option value="kwh_meter">Pemasangan KWH Meter</option>
            </select>
          </div>

          {/* Vertical Progress Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            {filteredProgress.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <History className="mx-auto mb-2 text-gray-400" size={32} />
                Tidak ada riwayat progress ditemukan
              </div>
            ) : (
              <div className="relative border-l-2 border-gray-200 ml-4 md:ml-6 pl-6 md:pl-8 space-y-8">
                {filteredProgress.map((item, index) => {
                  const stepColors = {
                    survey_install: 'bg-yellow-100 text-yellow-800 border-yellow-300',
                    pemberkasan: 'bg-orange-100 text-orange-800 border-orange-300',
                    installation: 'bg-blue-100 text-blue-800 border-blue-300',
                    survey_slo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
                    slo: 'bg-purple-100 text-purple-800 border-purple-300',
                    registrasi_pln: 'bg-teal-100 text-teal-800 border-teal-300',
                    documentation: 'bg-pink-100 text-pink-800 border-pink-300'
                  };
                  const stepLabels = {
                    survey_install: 'Survey Instalasi',
                    pemberkasan: 'Pemberkasan & Pengajuan PLN',
                    installation: 'Instalasi Listrik',
                    survey_slo: 'Survey SLO',
                    slo: 'Sertifikat SLO',
                    registrasi_pln: 'Registrasi Aplikasi PLN',
                    documentation: 'Dokumentasi & Laporan'
                  };

                  const colorClass = stepColors[item.step_name] || 'bg-gray-100 text-gray-800 border-gray-300';
                  const label = stepLabels[item.step_name] || item.step_name;

                  const stepDotColors = {
                    survey_install: 'border-yellow-500 text-yellow-600 bg-yellow-50',
                    pemberkasan: 'border-orange-500 text-orange-600 bg-orange-50',
                    installation: 'border-blue-500 text-blue-600 bg-blue-50',
                    survey_slo: 'border-indigo-500 text-indigo-600 bg-indigo-50',
                    slo: 'border-purple-500 text-purple-600 bg-purple-50',
                    registrasi_pln: 'border-teal-500 text-teal-600 bg-teal-50',
                    documentation: 'border-pink-500 text-pink-600 bg-pink-50'
                  };
                  const stepIcons = {
                    survey_install: MapPin,
                    pemberkasan: FileText,
                    installation: Zap,
                    survey_slo: MapPin,
                    slo: FileText,
                    registrasi_pln: Check,
                    documentation: Upload
                  };
                  
                  const dotColorClass = stepDotColors[item.step_name] || 'border-primary-500 text-primary-600 bg-primary-50';
                  const StepIcon = stepIcons[item.step_name] || Check;

                  return (
                    <div key={item.id} className="relative group">
                      {/* Timeline dot */}
                      <span className={`absolute -left-[40px] md:-left-[48px] mt-1 flex h-8 w-8 items-center justify-center rounded-full border-2 bg-white shadow-sm ${dotColorClass} transition-all duration-250 z-10 group-hover:scale-110`}>
                        <StepIcon size={14} className="stroke-[2.5]" />
                      </span>

                      {/* Content Card */}
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 hover:bg-white hover:shadow-md transition-all duration-200">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full border ${colorClass}`}>
                              {label}
                            </span>
                            <span className="text-gray-400">•</span>
                            <h4 className="text-sm font-semibold text-gray-900">{item.nama_pelanggan}</h4>
                            <span className="text-xs text-gray-500 font-mono">({item.nomor_meter})</span>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">
                            ⏱️ {formatDate(item.completed_at)}
                          </span>
                        </div>

                        {/* Details */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                            <User size={14} className="text-primary-600" />
                            <span>Teknisi / Pelaksana: <span className="text-gray-900 font-bold">{item.completed_by || 'Sistem'}</span></span>
                          </div>

                          {item.catatatan && (
                            <div className="bg-white border border-gray-150 rounded-lg p-3 text-sm text-gray-700 italic relative shadow-inner">
                              <span className="absolute top-1 left-2 text-primary-300 text-lg font-serif">“</span>
                              <p className="pl-4">{item.catatatan}</p>
                            </div>
                          )}

                          {/* Evidence Files */}
                          {item.evidence_files && item.evidence_files.length > 0 && (
                            <div className="pt-2">
                              <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                                <Upload size={12} className="text-emerald-600" />
                                Lampiran Bukti ({item.evidence_files.length})
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {item.evidence_files.map((file, i) => {
                                  const fn = file.split('/').pop();
                                  const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(fn);
                                  return (
                                    <a
                                      key={i}
                                      href={`${api}${file}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 bg-white hover:bg-primary-50 px-2.5 py-1.5 rounded-lg border border-gray-200 font-medium transition-all"
                                    >
                                      {isImg ? '🖼️' : '📄'} Bukti {i + 1}
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Action Link to main details */}
                          <div className="pt-2 flex justify-end">
                            <button
                              onClick={() => handleViewJobDetails(item.job)}
                              className="text-xs font-semibold text-primary-600 hover:text-primary-800 hover:underline flex items-center gap-1"
                            >
                              <Eye size={12} />
                              Lihat Detail Pekerjaan Lengkap
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manual Financial Transaction Modal */}
      {showManualTxModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transform scale-100 transition-all">
            {/* Modal Header */}
            <div className="p-6 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <DollarSign className="text-amber-500" />
                    Catat Keuangan Manual
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">Mencatat pemasukan/pengeluaran mandiri non-operasional</p>
                </div>
                <button
                  onClick={() => setShowManualTxModal(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200/50 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreateManualTx} className="p-6 space-y-5">
              {/* Type Switcher */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tipe Transaksi</label>
                <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-xl">
                  <button
                    type="button"
                    onClick={() => {
                      const newType = 'PEMASUKAN';
                      if (manualTxForm.job_id === 'STANDALONE') {
                        setManualTxForm({ ...manualTxForm, type: newType });
                      } else {
                        const matched = jobs.find(j => j.id === manualTxForm.job_id);
                        const defaultNominal = matched ? (matched.tarif_pemasangan || '') : '';
                        setManualTxForm({
                          ...manualTxForm,
                          type: newType,
                          nominal: defaultNominal.toString()
                        });
                      }
                    }}
                    className={`py-2 px-3 rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 transition-all ${
                      manualTxForm.type === 'PEMASUKAN'
                        ? 'bg-emerald-600 text-white shadow'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
                    }`}
                  >
                    <ArrowUpRight size={16} />
                    Pemasukan
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newType = 'PENGELUARAN';
                      if (manualTxForm.job_id === 'STANDALONE') {
                        setManualTxForm({ ...manualTxForm, type: newType });
                      } else {
                        const matched = jobs.find(j => j.id === manualTxForm.job_id);
                        const defaultNominal = matched
                          ? (matched.gaji_teknisi !== undefined && matched.gaji_teknisi !== null
                              ? matched.gaji_teknisi
                              : (matched.tarif_pemasangan ? Math.round(matched.tarif_pemasangan * 0.40) : ''))
                          : '';
                        setManualTxForm({
                          ...manualTxForm,
                          type: newType,
                          nominal: defaultNominal.toString()
                        });
                      }
                    }}
                    className={`py-2 px-3 rounded-lg text-sm font-bold flex items-center justify-center gap-1.5 transition-all ${
                      manualTxForm.type === 'PENGELUARAN'
                        ? 'bg-rose-600 text-white shadow'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
                    }`}
                  >
                    <ArrowDownLeft size={16} />
                    Pengeluaran
                  </button>
                </div>
              </div>

              {/* Job Dropdown Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pilih Pekerjaan / Kategori</label>
                <select
                  value={manualTxForm.job_id}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'STANDALONE') {
                      setManualTxForm(prev => ({
                        ...prev,
                        job_id: 'STANDALONE',
                        nama_transaksi: '',
                        nominal: ''
                      }));
                    } else {
                      const matched = jobs.find(j => j.id === val);
                      if (matched) {
                        const defaultNominal = manualTxForm.type === 'PEMASUKAN'
                          ? (matched.tarif_pemasangan || '')
                          : (matched.gaji_teknisi !== undefined && matched.gaji_teknisi !== null
                              ? matched.gaji_teknisi
                              : (matched.tarif_pemasangan ? Math.round(matched.tarif_pemasangan * 0.40) : ''));
                        setManualTxForm(prev => ({
                          ...prev,
                          job_id: val,
                          nama_transaksi: `Pekerjaan: ${matched.nama_pelanggan} - ${matched.jenis_instalasi}`,
                          nominal: defaultNominal.toString()
                        }));
                      }
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-sm"
                >
                  <option value="STANDALONE">-- Transaksi Mandiri (Ketik Manual) --</option>
                  {jobs
                    .filter(j => j.jenis_instalasi !== 'PEMASUKAN_MANUAL' && j.jenis_instalasi !== 'PENGELUARAN_MANUAL')
                    .map(j => (
                      <option key={j.id} value={j.id}>
                        {j.nama_pelanggan} - {j.jenis_instalasi} (Meter: {j.nomor_meter || '-'}) [{j.status === 'completed' ? 'Selesai' : 'Aktif'}]
                      </option>
                    ))}
                </select>
              </div>

              {/* Transaction Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nama Transaksi / Deskripsi</label>
                <input
                  type="text"
                  required
                  disabled={manualTxForm.job_id !== 'STANDALONE'}
                  placeholder={manualTxForm.type === 'PEMASUKAN' ? 'Contoh: Pembayaran instalasi khusus, dll.' : 'Contoh: Pembelian kabel roll, pipa PVC, dll.'}
                  value={manualTxForm.nama_transaksi}
                  onChange={(e) => setManualTxForm({ ...manualTxForm, nama_transaksi: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Nominal */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nominal (Rp)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm">Rp</span>
                    <input
                      type="number"
                      required
                      placeholder="0"
                      value={manualTxForm.nominal}
                      onChange={(e) => setManualTxForm({ ...manualTxForm, nominal: e.target.value })}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal</label>
                  <input
                    type="date"
                    required
                    value={manualTxForm.tanggal}
                    onChange={(e) => setManualTxForm({ ...manualTxForm, tanggal: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catatan Detail (Keterangan)</label>
                <textarea
                  rows="3"
                  placeholder="Tambahkan catatan rincian transaksi jika diperlukan..."
                  value={manualTxForm.catatatan}
                  onChange={(e) => setManualTxForm({ ...manualTxForm, catatatan: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowManualTxModal(false)}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSavingManualTx}
                  className={`px-5 py-2 text-sm font-bold text-white rounded-lg shadow transition-colors flex items-center gap-1.5 ${
                    isSavingManualTx
                      ? 'bg-gray-400 cursor-not-allowed'
                      : manualTxForm.type === 'PEMASUKAN'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-rose-600 hover:bg-rose-700'
                  }`}
                >
                  {isSavingManualTx ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Simpan Transaksi
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Job Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Buat Job Baru</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            <form onSubmit={handleCreateJob} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nomor Registrasi <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newJob.nomor_registrasi}
                    onChange={(e) => setNewJob({ ...newJob, nomor_registrasi: e.target.value, nomor_meter: e.target.value })}
                    required
                    placeholder="Contoh: 2026/REG/0001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nama Pelanggan</label>
                  <input
                    type="text"
                    value={newJob.nama_pelanggan}
                    onChange={(e) => setNewJob({ ...newJob, nama_pelanggan: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alamat</label>
                  <input
                    type="text"
                    value={newJob.alamat}
                    onChange={(e) => setNewJob({ ...newJob, alamat: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No. HP</label>
                  <input
                    type="text"
                    value={newJob.no_hp}
                    onChange={(e) => setNewJob({ ...newJob, no_hp: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nomor KTP (NIK)</label>
                  <input
                    type="text"
                    value={newJob.nomor_ktp}
                    onChange={(e) => setNewJob({ ...newJob, nomor_ktp: e.target.value })}
                    placeholder="16 digit NIK"
                    maxLength="16"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID Pelanggan PLN
                    {newJob.keperluan === 'TAMBAH_DAYA' && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    type="text"
                    value={newJob.id_pelanggan_pln}
                    onChange={(e) => setNewJob({ ...newJob, id_pelanggan_pln: e.target.value })}
                    placeholder="12 digit ID PLN"
                    maxLength="12"
                    required={newJob.keperluan === 'TAMBAH_DAYA'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NPWP</label>
                  <input
                    type="text"
                    value={newJob.npwp}
                    onChange={(e) => setNewJob({ ...newJob, npwp: e.target.value })}
                    placeholder="Untuk pelanggan bisnis"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pemasukan <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={newJob.tarif_pemasangan}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewJob({
                        ...newJob,
                        tarif_pemasangan: val,
                        gaji_teknisi: val ? Math.round(val * 0.40) : ''
                      });
                    }}
                    placeholder="Contoh: 750000"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pengeluaran
                  </label>
                  <input
                    type="number"
                    value={newJob.gaji_teknisi}
                    onChange={(e) => setNewJob({ ...newJob, gaji_teknisi: e.target.value })}
                    placeholder="Contoh: 300000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Daya Listrik (baru)</label>
                  {showManualDaya ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newJob.daya_listrik}
                        onChange={(e) => setNewJob({ ...newJob, daya_listrik: e.target.value })}
                        placeholder="Contoh: 7700VA / 19700VA"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => { setShowManualDaya(false); setNewJob({ ...newJob, daya_listrik: '900VA' }); }}
                        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                        title="Kembali ke daftar VA"
                      >
                        Daftar
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <select
                        value={newJob.daya_listrik}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__manual__') {
                            setShowManualDaya(true);
                            setNewJob({ ...newJob, daya_listrik: '' });
                          } else {
                            setNewJob({ ...newJob, daya_listrik: v });
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="450VA">450 VA</option>
                        <option value="900VA">900 VA</option>
                        <option value="1300VA">1300 VA</option>
                        <option value="2200VA">2200 VA</option>
                        <option value="3500VA">3500 VA</option>
                        <option value="4400VA">4400 VA</option>
                        <option value="5500VA">5500 VA</option>
                        <option value="6600VA">6600 VA</option>
                        <option value="7700VA">7700 VA</option>
                        <option value="10600VA">10600 VA</option>
                        <option value="11000VA">11000 VA</option>
                        <option value="13200VA">13200 VA</option>
                        <option value="16500VA">16500 VA</option>
                        <option value="19700VA">19700 VA</option>
                        <option value="23000VA">23000 VA</option>
                        <option value="__manual__">Lainnya (input manual)…</option>
                      </select>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Daya Listrik (lama)</label>
                  <input
                    type="text"
                    value={newJob.daya_lama}
                    onChange={(e) => setNewJob({ ...newJob, daya_lama: e.target.value })}
                    placeholder="Contoh: 1300VA (kosongkan jika pasang baru)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Keperluan</label>
                  <select
                    value={newJob.keperluan}
                    onChange={(e) => setNewJob({ ...newJob, keperluan: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Pilih Keperluan --</option>
                    <option value="PASANG_BARU">Pasang Baru</option>
                    <option value="TAMBAH_DAYA">Tambah Daya</option>
                  </select>
                </div>
                {newJob.keperluan === 'TAMBAH_DAYA' && (
                  <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <div className="text-amber-600 mt-0.5">⚠</div>
                      <div className="text-sm text-amber-800 flex-1">
                        <p className="font-semibold mb-1">Tambah Daya — mohon pastikan:</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>ID Pelanggan PLN wajib diisi (12 digit).</li>
                          <li>Daya Listrik (lama) dan Daya Listrik (baru) tidak boleh sama.</li>
                          <li>Riwayat perubahan daya akan tercatat otomatis di sistem.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Instalasi</label>
                  <select
                    value={newJob.jenis_instalasi}
                    onChange={(e) => setNewJob({ ...newJob, jenis_instalasi: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="BARU">Baru</option>
                    <option value="UBAH_DAYA">Ubah Daya</option>
                    <option value="SPEEDY">Speedy/Internet</option>
                    <option value="UBAH_NAMA">Ubah Nama</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                  <input
                    type="text"
                    value={newJob.lokasi_latitude}
                    onChange={(e) => setNewJob({ ...newJob, lokasi_latitude: e.target.value })}
                    placeholder="Contoh: -6.2088"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                  <input
                    type="text"
                    value={newJob.lokasi_longitude}
                    onChange={(e) => setNewJob({ ...newJob, lokasi_longitude: e.target.value })}
                    placeholder="Contoh: 106.8456"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
                  <textarea
                    value={newJob.catatatan}
                    onChange={(e) => setNewJob({ ...newJob, catatatan: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Job Detail Modal */}
      {showDetailModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Detail Job</h2>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* Job Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Nomor Meter</p>
                  <p className="font-semibold text-gray-900">{selectedJob.nomor_meter}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Nama Pelanggan</p>
                  <p className="font-semibold text-gray-900">{selectedJob.nama_pelanggan}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Alamat</p>
                  <p className="font-semibold text-gray-900">{selectedJob.alamat}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">No. HP</p>
                  <p className="font-semibold text-gray-900">{selectedJob.no_hp || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Daya Listrik</p>
                  <p className="font-semibold text-gray-900">{selectedJob.daya_listrik || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Jenis Instalasi</p>
                  <p className="font-semibold text-gray-900">{selectedJob.jenis_instalasi}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedJob.status)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">Progress</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-600 rounded-full transition-all"
                        style={{ width: `${selectedJob.progress || 0}%` }}
                      />
                    </div>
                    <span className="font-semibold">{selectedJob.progress || 0}%</span>
                  </div>
                </div>
              </div>

              {/* Job Steps */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Tahapan Pekerjaan</h3>
                <div className="space-y-3">
                  {jobStages.map((stage, index) => {
                    const step = selectedJob.steps?.find(s => s.step_name === stage.key);
                    const isCompleted = step?.is_completed || false;
                    // Role-based canUpdate logic
                    // - OWNER, IT, TECHNICIAN: full access (superuser)
                    // - MANAGER: only pascapemasangan steps (pemberkasan, slo, registrasi_pln, documentation)
                    // - INSTALLER: installation step only (if assigned)
                    // - SURVEYOR: survey_install, survey_slo steps (if assigned)
                    // - SLO: slo step only (if assigned)
                    const managerSteps = ['pemberkasan', 'slo', 'registrasi_pln', 'documentation'];
                    const isAssigned = selectedJob.assigned_to === user.id ||
                      selectedJob.assigned_technicians?.some(t => t.id === user.id);

                    // Check if user has permission for this specific step
                    const hasStepPermission = isStepAllowedForCurrentUser(user, stage.key);

                    const canUpdate = isAssigned && hasStepPermission;

                    return (
                      <div key={stage.key} className={`border rounded-lg p-4 ${isCompleted ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                              {isCompleted ? <CheckCircle size={18} /> : <span className="text-sm font-medium">{index + 1}</span>}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{stage.label}</p>
                              <p className="text-sm text-gray-500">{step?.description || ''}</p>
                              <p className="text-xs text-gray-400 mt-1">Bobot: {stage.weight}%</p>
                            </div>
                          </div>
                          {canUpdate && selectedJob.status !== 'completed' && (
                            <button
                              onClick={() => handleUpdateProgress(stage.key, !isCompleted)}
                              className={`px-3 py-1 rounded-lg text-sm ${isCompleted ? 'bg-gray-200 hover:bg-gray-300' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                            >
                              {isCompleted ? 'Batal Selesai' : 'Tandai Selesai'}
                            </button>
                          )}
                        </div>
                        {step?.evidence_files?.length > 0 && (
                          <div className="mt-3 ml-11">
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <Upload size={12} className="text-green-600" />
                              Bukti Upload ({step.evidence_files.length} file)
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                              {step.evidence_files.map((file, i) => {
                                const fileName = file.split('/').pop();
                                const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                                const isPdf = /\.pdf$/i.test(fileName);

                                return (
                                  <div
                                    key={i}
                                    className="group relative border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
                                  >
                                    {isImage ? (
                                      <div className="aspect-square bg-gray-100 relative">
                                        <img
                                          src={`${api}${file}`}
                                          alt={`Bukti ${i + 1}`}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="%239ca3af"%3E%3Cpath stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /%3E%3C/svg%3E';
                                          }}
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                                          <a
                                            href={`${api}${file}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 bg-white rounded-full text-gray-700 hover:text-primary-600 transform scale-90 group-hover:scale-100 transition-transform"
                                          >
                                            <Eye size={18} />
                                          </a>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="aspect-square bg-gray-50 flex flex-col items-center justify-center p-3">
                                        {isPdf ? (
                                          <FileText size={40} className="text-red-500 mb-2" />
                                        ) : (
                                          <FileText size={40} className="text-gray-400 mb-2" />
                                        )}
                                        <p className="text-xs text-center text-gray-600 truncate w-full font-medium">
                                          {fileName.length > 15 ? fileName.substring(0, 15) + '...' : fileName}
                                        </p>
                                        <p className="text-xs text-gray-400">{isPdf ? 'PDF' : 'File'}</p>
                                      </div>
                                    )}
                                    <div className="p-2 border-t border-gray-100 bg-gray-50">
                                      <a
                                        href={`${api}${file}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
                                      >
                                        <Download size={12} />
                                        Lihat
                                      </a>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {canUpdate && selectedJob.status !== 'completed' && (
                          <div className="mt-3 ml-11">
                            <label className={`flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed ${uploadingStep === stage.key ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-primary-500 hover:bg-gray-50'} rounded-lg p-3 transition-all`}>
                              {uploadingStep === stage.key ? (
                                <>
                                  <RefreshCw size={16} className="text-blue-600 animate-spin" />
                                  <span className="text-sm text-blue-600 font-medium">Mengupload...</span>
                                </>
                              ) : (
                                <>
                                  <Upload size={16} className="text-gray-500" />
                                  <span className="text-sm text-gray-600 font-medium">+ Tambah Bukti</span>
                                  <span className="text-xs text-gray-400">(JPG, PNG, PDF)</span>
                                </>
                              )}
                              <input
                                type="file"
                                accept="image/jpeg,image/png,application/pdf"
                                multiple
                                className="hidden"
                                disabled={uploadingStep === stage.key}
                                onChange={(e) => {
                                  if (e.target.files && e.target.files.length > 0) {
                                    handleUploadEvidence(stage.key, Array.from(e.target.files));
                                  }
                                }}
                              />
                            </label>
                          </div>
                        )}
                        {/* Per-step data panel */}
                        {(() => {
                          const stepDataItem = selectedJob.step_data?.[stage.key] || {};
                          if (!canUpdate && !stepDataItem.instalasi_choice && !stepDataItem.assigned_technician_name && !stepDataItem.registrasi_pln_number && !stepDataItem.checklist) {
                            return null;
                          }
                          const checklistItems = getChecklistForStage(stage.key);
                          const selectedChecklist = stepDataItem.checklist || {};
                          return (
                            <div className="mt-3 ml-11 bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                              {stage.key === 'installation' && (
                                <div>
                                  <p className="text-xs font-medium text-gray-700 mb-1">Pelaksana Instalasi:</p>
                                  {stepDataItem.instalasi_choice && (
                                    <p className="text-sm text-gray-800">
                                      {stepDataItem.instalasi_choice === 'pribadi' ? 'Pelanggan Melakukan Sendiri (Pribadi)' : 'Petugas PLN / Vendor'}
                                    </p>
                                  )}
                                  {isManager && (
                                    <div className="flex gap-2 mt-2">
                                      <button
                                        type="button"
                                        onClick={() => handleSetStepData(stage.key, { instalasi_choice: 'pribadi' })}
                                        className={`px-2 py-1 text-xs rounded ${stepDataItem.instalasi_choice === 'pribadi' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                                      >Pribadi</button>
                                      <button
                                        type="button"
                                        onClick={() => handleSetStepData(stage.key, { instalasi_choice: 'petugas' })}
                                        className={`px-2 py-1 text-xs rounded ${stepDataItem.instalasi_choice === 'petugas' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                                      >Petugas</button>
                                    </div>
                                  )}
                                </div>
                              )}
                              {(stage.key === 'installation' && stepDataItem.instalasi_choice === 'petugas') && stepDataItem.assigned_technician_name && (
                                <div>
                                  <p className="text-xs font-medium text-gray-700">Teknisi/Installer:</p>
                                  <p className="text-sm text-gray-800">{stepDataItem.assigned_technician_name}</p>
                                </div>
                              )}
                              {checklistItems.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-gray-700 mb-1">Checklist:</p>
                                  <div className="space-y-1">
                                    {checklistItems.map((citem) => {
                                      const isChecked = !!selectedChecklist[citem.key];
                                      return (
                                        <label key={citem.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            disabled={!canUpdate}
                                            onChange={(e) => handleSetStepData(stage.key, { checklist: { ...selectedChecklist, [citem.key]: e.target.checked } })}
                                            className="rounded text-primary-600 focus:ring-primary-500"
                                          />
                                          {citem.label}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {stage.key === 'registrasi_pln' && (
                                <div>
                                  <p className="text-xs font-medium text-gray-700">No. Aplikasi PLN:</p>
                                  {stepDataItem.registrasi_pln_number ? (
                                    <p className="text-sm text-gray-800 font-mono">{stepDataItem.registrasi_pln_number}</p>
                                  ) : isManager && (
                                    <input
                                      type="text"
                                      placeholder="misal: 12345-2025"
                                      className="mt-1 w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      onBlur={(e) => e.target.value && handleSetStepData(stage.key, { registrasi_pln_number: e.target.value })}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {step?.completed_at && (
                          <p className="mt-2 ml-11 text-xs text-gray-500">
                            Selesai: {formatDate(step.completed_at)}
                            {step.completed_by && ` oleh ${step.completed_by}`}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Job Metadata */}
              <div className="text-sm text-gray-500 space-y-1 pt-4 border-t">
                <p>Dibuat: {formatDate(selectedJob.created_at)}</p>
                <p>Oleh: {selectedJob.created_by_name}</p>
                {selectedJob.assigned_technicians && selectedJob.assigned_technicians.length > 0 ? (
                  <div className="pt-2">
                    <p className="font-medium text-gray-700 mb-1">Ditugaskan ke:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {selectedJob.assigned_technicians.map((t, idx) => (
                        <li key={idx}>
                          {t.nama_lengkap || t.username}
                          {t.assigned_role && (
                            <span className="ml-1 text-xs text-blue-600 font-medium">
                              ({getRoleLabel(t.assigned_role)})
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {isManager && selectedJob.status !== 'completed' && (
                      <button
                        onClick={() => {
                          const existing = {};
                          selectedJob.assigned_technicians.forEach((t) => {
                            existing[t.id] = t.assigned_role || 'TECHNICIAN';
                          });
                          setTechnicianRoles(existing);
                          setEditingAssignment(true);
                          setShowDetailModal(false);
                          setShowAssignModal(true);
                        }}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Edit size={14} /> Edit Penugasan
                      </button>
                    )}
                  </div>
                ) : (
                  selectedJob.assigned_to_name && (
                    <p>Ditugaskan ke: {selectedJob.assigned_to_name}</p>
                  )
                )}
                {selectedJob.completed_at && <p>Selesai: {formatDate(selectedJob.completed_at)}</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-role Assignment Modal */}
      {showAssignModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingAssignment ? 'Edit Penugasan' : 'Tambah Teknisi ke Job'}
                </h2>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setTechnicianRoles({});
                    setSelectedTechId('');
                    setSelectedRole('TECHNICIAN');
                    setEditingAssignment(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Job:</strong> {selectedJob.nomor_meter}<br/>
                  <strong>Pelanggan:</strong> {selectedJob.nama_pelanggan}<br/>
                  <strong>Jenis:</strong> {selectedJob.jenis_instalasi}
                </p>
              </div>

              <p className="text-gray-600 mb-4">Tentukan teknisi dan peran masing-masing (lebih dari 1 diperbolehkan):</p>

              {/* List of assigned technicians with roles */}
              {Object.keys(technicianRoles).length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-700 mb-2">List technicians yang sudah ditugaskan:</h3>
                  <div className="border border-gray-200 rounded-lg p-3 max-h-60 overflow-y-auto bg-gray-50">
                    {Object.entries(technicianRoles).map(([techId, role]) => {
                      const tech = technicians.find(t => t.id === techId);
                      return (
                        <div key={techId} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 mb-2 last:mb-0">
                          <div className="min-w-0 flex-1 mr-3">
                            <p className="font-medium text-gray-900 truncate">{tech?.nama_lengkap || tech?.username}</p>
                            {tech?.kategori_teknisi && (
                              <p className="text-xs text-gray-500">Kategori: {tech.kategori_teknisi}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-3">
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                              {getRoleLabel(role)}
                            </span>
                            <button
                              onClick={() => removeTechnician(techId)}
                              className="text-red-500 hover:text-red-700 text-sm px-2"
                              title="Hapus"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Technician selection form */}
              <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <p className="text-sm font-medium text-gray-700 mb-3">Tambahkan teknisi baru:</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Technician Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pilih teknisi</label>
                    <select
                      value={selectedTechId}
                      onChange={(e) => setSelectedTechId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">-- Pilih teknisi --</option>
                      {technicians.map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.nama_lengkap || tech.username} {tech.kategori_teknisi ? `(${tech.kategori_teknisi})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Role Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Peran teknisi</label>
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="SURVEYOR">Surveyor (tahap survei)</option>
                      <option value="INSTALLER">Installer/Pemasang</option>
                      <option value="SLO">Petugas SLO</option>
                      <option value="TECHNICIAN">Teknisi Umum</option>
                    </select>
                  </div>
                </div>

                {Object.keys(technicianRoles).length > 0 && (
                  <p className="mt-2 text-xs text-gray-600">
                    <strong>Ringkasan:</strong> {Object.keys(technicianRoles).length} teknisi ({Object.values(technicianRoles).filter(r => r === 'INSTALLER').length} installer, {Object.values(technicianRoles).filter(r => r === 'SURVEYOR').length} surveyor, {Object.values(technicianRoles).filter(r => r === 'SLO').length} slo)
                  </p>
                )}

                <button
                  onClick={addToAssignment}
                  disabled={!selectedTechId || !selectedRole}
                  className={`mt-4 px-6 py-2 rounded-lg font-medium transition-colors ${
                    selectedTechId && selectedRole
                      ? 'bg-primary-600 hover:bg-primary-700 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Tambahkan ke daftar
                </button>
              </div>

              {Object.keys(technicianRoles).length === 0 && (
                <div className="py-8 text-center text-gray-500">
                          Belum ada teknisi yang ditugaskan<br/>
                          Pilih teknisi dan peran, lalu klik "Tambahkan ke daftar"
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={confirmMultiAssign}
                  disabled={Object.keys(technicianRoles).length === 0}
                  className={`flex-1 px-6 py-3 rounded-lg font-medium text-white transition-colors ${
                    Object.keys(technicianRoles).length > 0
                      ? 'bg-primary-600 hover:bg-primary-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
  {editingAssignment ? 'Simpan Perubahan' : 'Simpan Penugasan'}
                </button>
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setTechnicianRoles({});
                    setSelectedTechId('');
                    setSelectedRole('TECHNICIAN');
                    setEditingAssignment(false);
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Batal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Update Modal */}
      {showPaymentModal && selectedPaymentJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full border border-gray-100 overflow-hidden transform scale-100 transition-all">
            <div className="p-6 border-b border-gray-200 bg-primary-50">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <DollarSign className="text-primary-600" />
                  Keuangan & Pembayaran
                </h2>
                <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 border border-gray-150 rounded-lg p-3 text-sm text-gray-700">
                <p className="font-semibold text-gray-900">{selectedPaymentJob.nama_pelanggan}</p>
                <p className="text-xs text-gray-500">Meter: {selectedPaymentJob.nomor_meter} | Daya: {selectedPaymentJob.daya_listrik}</p>
                <p className="text-xs text-gray-500 mt-1">Jenis: {selectedPaymentJob.jenis_instalasi} | Teknisi: {selectedPaymentJob.assigned_to_name || 'Belum ditugaskan'}</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Pemasukan
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold">Rp</span>
                  <input
                    type="number"
                    value={paymentForm.tarif_pemasangan}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPaymentForm({
                        ...paymentForm,
                        tarif_pemasangan: val,
                        gaji_teknisi: val ? Math.round(val * 0.40) : ''
                      });
                    }}
                    required
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-semibold"
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Mengubah pemasukan ini akan otomatis mengkalkulasi pengeluaran sebesar 40%.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Pengeluaran
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold">Rp</span>
                  <input
                    type="number"
                    value={paymentForm.gaji_teknisi}
                    onChange={(e) => setPaymentForm({ ...paymentForm, gaji_teknisi: e.target.value })}
                    required
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-semibold text-rose-600"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Status Pembayaran</p>
                  <p className="text-xs text-gray-500">Konfirmasi status lunas pemasangan</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentForm.dikonfirmasi}
                    onChange={(e) => setPaymentForm({ ...paymentForm, dikonfirmasi: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              {paymentForm.dikonfirmasi && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Pembayaran</label>
                  <input
                    type="date"
                    value={paymentForm.tanggal_pembayaran}
                    onChange={(e) => setPaymentForm({ ...paymentForm, tanggal_pembayaran: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catatan Keuangan</label>
                <textarea
                  value={paymentForm.catatan}
                  onChange={(e) => setPaymentForm({ ...paymentForm, catatan: e.target.value })}
                  rows={2}
                  placeholder="Contoh: Pembayaran transfer bank mandiri..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Batal
                </button>
                <button
                  onClick={() => {
                    handleUpdatePayment(selectedPaymentJob.id, {
                      tarif_pemasangan: paymentForm.tarif_pemasangan,
                      gaji_teknisi: paymentForm.gaji_teknisi,
                      dikonfirmasi: paymentForm.dikonfirmasi,
                      tanggal_pembayaran: paymentForm.tanggal_pembayaran,
                      catatan: paymentForm.catatan
                    });
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 shadow-sm"
                >
                  Simpan Transaksi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicalJobsPage;
